import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';

import {
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   Notification,
   NotificationSize,
   NotificationType,
   ShowModalEventInterceptor,
   StyledFlex,
} from '@dreryk/react-components';

import { RefundAdvancePaymentForm } from '../form/RefundAdvancePaymentForm';
import { formInitializer } from '../form/RefundAdvancePaymentForm.utils';
import { refundAdvancePaymentTranslationKeys } from '../translations';

import { PatientInformationView } from './patientInformationView/PatientInformationView';
import { AppointmentAdvancePaymentRefundAction } from './actions/AppointmentAdvancePaymentRefundActions';
import { OnRefundSuccessCallback } from './RefundAdvancePaymentShowModalEventInterceptor.types';

const t = refundAdvancePaymentTranslationKeys.modal;

type RefundAdvancePaymentShowModalEventInterceptorProps = Readonly<{
   advancePayment: FinancialSettlementAdvancePayment;
   beginDate: Date;
   endDate: Date;
   children: JSX.Element;
   patientFirstName: string | null;
   patientLastName: string | null;
   totalPriceAmount?: number;
   onSuccess?: OnRefundSuccessCallback;
   onCancel?: VoidFunction;
}>;

export const RefundAdvancePaymentShowModalEventInterceptor = (
   props: RefundAdvancePaymentShowModalEventInterceptorProps
): JSX.Element => {
   return (
      <ShowModalEventInterceptor
         shouldShowModal
         title={t.title}
         testId={'refundAdvancePaymentShowModal'}
         footerItemsHidden
         modalWidth={ModalWidth.NORMAL}
         content={({ resolve }) => (
            <StyledFlex gap={18} direction={'column'}>
               <PatientInformationView
                  patientFirstName={props.patientFirstName}
                  patientLastName={props.patientLastName}
                  beginDate={props.beginDate}
                  endDate={props.endDate}
               />

               <Notification
                  testId={'refundNotification'}
                  type={NotificationType.INFO}
                  label={t.notificationContent}
                  size={NotificationSize.SMALLER}
               />

               <RefundAdvancePaymentForm initializer={formInitializer(props.totalPriceAmount, props.advancePayment)}>
                  {({ formState, handleSubmit }) => (
                     <>
                        <AppointmentAdvancePaymentRefundAction
                           advancePaymentId={props.advancePayment.initialTransactionId}
                           advancePaymentTransactionId={props.advancePayment.transactionExternalId!}
                           onSuccess={(updateRemainingAmount, advancePaymentId) => {
                              props.onSuccess?.(updateRemainingAmount, advancePaymentId);
                              resolve();
                           }}
                        >
                           {({ refundAppointmentAdvancePayment }) => (
                              <ModalConfirmButton
                                 label={t.actions.confirm.label}
                                 onClick={handleSubmit(refundAppointmentAdvancePayment)}
                                 blockingOperation={t.actions.confirm.pendingMessage}
                              />
                           )}
                        </AppointmentAdvancePaymentRefundAction>

                        <ModalCancelButton
                           onClick={() => {
                              resolve();
                              props.onCancel?.();
                           }}
                           shouldConfirmExit={formState.isDirty}
                        />
                        <ModalCloseButton
                           onClick={() => {
                              resolve();
                              props.onCancel?.();
                           }}
                           shouldConfirmExit={formState.isDirty}
                        />
                     </>
                  )}
               </RefundAdvancePaymentForm>
            </StyledFlex>
         )}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
